import { useEffect, useState } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

interface Testimonial {
  id: string;
  author_name: string;
  author_title: string | null;
  company: string | null;
  content: string;
  rating: number;
}

// Fallback static testimonials
const fallbackTestimonials = [
  {
    id: "1",
    author_name: "Mgr. Janka Bittó Cigániková, MBA",
    author_title: "poslankyňa NR SR",
    company: null,
    content: "Veľmi milé prekvapenie je pre mňa nadštandardná rýchlosť a profesionalita, s ktorou som sa u Vás stretla. Oceňujem aj snahu poradiť. Uľahčili ste mi život :)",
    rating: 5,
  },
  {
    id: "2",
    author_name: "Ramadhatun K. Nugraheny",
    author_title: null,
    company: "Veľvyslanectvo Indonézskej republiky",
    content: "V mene Indonézskeho Veľvyslanectva v Bratislave, by som sa Vám chcela úprimne poďakovať za Vaše služby. Tešíme sa na úspešnú spoluprácu.",
    rating: 5,
  },
  {
    id: "3",
    author_name: "Dimitar Alexiev",
    author_title: "člen dozornej rady",
    company: "Heineken Slovensko a.s.",
    content: "Touto cestou by som Vám chcel vyjadriť uznanie a poďakovať za rýchlosť a kvalitu Vašich služieb pri legalizácii mojich dokumentov.",
    rating: 5,
  },
];

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <div className="bg-card rounded-3xl border border-border p-8 hover:shadow-xl transition-shadow relative h-full">
      {/* Quote Icon */}
      <div className="absolute -top-4 right-8 w-10 h-10 bg-secondary rounded-xl flex items-center justify-center">
        <Quote className="w-5 h-5 text-secondary-foreground" />
      </div>

      {/* Rating */}
      <div className="flex gap-1 mb-6">
        {Array.from({ length: testimonial.rating }).map((_, i) => (
          <Star key={i} className="w-5 h-5 fill-warning text-warning" />
        ))}
      </div>

      {/* Content */}
      <p className="text-foreground leading-relaxed mb-6">
        &ldquo;{testimonial.content}&rdquo;
      </p>

      {/* Author */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-gradient-to-br from-secondary to-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
          {testimonial.author_name.charAt(0)}
        </div>
        <div>
          <div className="font-semibold text-foreground">{testimonial.author_name}</div>
          <div className="text-sm text-muted-foreground">
            {[testimonial.author_title, testimonial.company].filter(Boolean).join(", ")}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>(fallbackTestimonials);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const { data, error } = await supabase
          .from("testimonials")
          .select("id, author_name, author_title, company, content, rating")
          .eq("is_active", true)
          .order("sort_order", { ascending: true });

        if (error) throw error;
        if (data && data.length > 0) {
          setTestimonials(data);
        }
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  const useCarousel = testimonials.length > 6;

  return (
    <section className="py-24 bg-muted/30" aria-labelledby="testimonials-heading">
      <div className="container mx-auto px-4">
        {/* Header */}
        <header className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-secondary font-semibold text-sm uppercase tracking-wider mb-4">
            {t('testimonials.badge', 'Recenzie')}
          </span>
          <h2 id="testimonials-heading" className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
            {t('testimonials.title', 'Čo o nás hovoria klienti')}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t('testimonials.subtitle', 'Prečítajte si referencie od našich spokojných zákazníkov.')}
          </p>
        </header>

        {/* Testimonials */}
        {isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card rounded-3xl border border-border p-8">
                <Skeleton className="h-5 w-32 mb-6" />
                <Skeleton className="h-20 w-full mb-6" />
                <div className="flex items-center gap-4">
                  <Skeleton className="w-12 h-12 rounded-full" />
                  <div>
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : useCarousel ? (
          <div className="px-12">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {testimonials.map((testimonial) => (
                  <CarouselItem key={testimonial.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                    <TestimonialCard testimonial={testimonial} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-0" />
              <CarouselNext className="right-0" />
            </Carousel>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
